import { obterChave } from './chave'

/**
 * A prova DPoP (RFC 9449, faixa 33 §3–§4): JWS compacto ES256 assinado pela chave do dispositivo. Sem axios nem
 * socket — quem decide QUANDO provar é o interceptor (`lib/api/client.ts`) e o socket (`lib/socket.ts`).
 * Ver `prova.md`.
 */
export interface PedidoDeProva {
  /** Método do pedido; vai MAIÚSCULO no claim. */
  htm: string
  /** URL do pedido sem query nem fragment — ver `htuDe`. */
  htu: string
  /** Access que acompanha o pedido: vira `ath`. Ausente no login e no refresh. */
  accessToken?: string
}

export type ResultadoDaProva = { tipo: 'prova'; prova: string } | { tipo: 'sem-suporte' } | { tipo: 'falhou' }

/** O jwk público do header: SÓ estes quatro campos (com `d` = `invalid_dpop_proof`, faixa 33 §2). */
interface JwkPublico {
  kty: string
  crv: string
  x: string
  y: string
}

/** Último `DPoP-Nonce` de qualquer resposta (faixa 33 §4): um valor global, nem por rota nem por sessão. */
let nonce: string | null = null

/**
 * Espelho no `localStorage`: sem ele o recarregamento zera o módulo e o 1º pedido do boot (o refresh) sai sem
 * nonce → 400 `use_dpop_nonce` + repetição em toda carga. O nonce não é segredo (o back dá um a qualquer pedido que
 * fale DPoP) nem é por sessão; vencido (> 300 s) só volta ao custo de antes: uma repetição.
 */
const CHAVE_DO_STORAGE = 'omni.dpop.nonce'

export function guardarNonce(valor: string): void {
  if (valor.trim() === '') return
  nonce = valor
  try {
    localStorage.setItem(CHAVE_DO_STORAGE, valor)
  } catch {
    // storage bloqueado/cheio: fica só na memória (o custo é o de antes, uma repetição no próximo boot)
  }
}

export function lerNonce(): string | null {
  if (nonce === null) nonce = nonceGuardado()
  return nonce
}

function nonceGuardado(): string | null {
  try {
    const valor = localStorage.getItem(CHAVE_DO_STORAGE)
    return valor !== null && valor.trim() !== '' ? valor : null
  } catch {
    return null
  }
}

/** Calculado 1x por chave: a pública não muda enquanto o objeto vive. */
const jwkPorChave = new WeakMap<CryptoKey, Promise<JwkPublico>>()

export async function montarProva({ htm, htu, accessToken }: PedidoDeProva): Promise<ResultadoDaProva> {
  const chave = await obterChave()
  if (chave.tipo !== 'chave') return chave
  try {
    const header = { typ: 'dpop+jwt', alg: 'ES256', jwk: await jwkPublico(chave.par.publicKey) }
    const claims: Record<string, string | number> = {
      jti: crypto.randomUUID(),
      htm: htm.toUpperCase(),
      htu,
      iat: Math.floor(Date.now() / 1000),
    }
    const atual = lerNonce()
    if (atual !== null) claims.nonce = atual
    if (accessToken !== undefined) claims.ath = base64url(await crypto.subtle.digest('SHA-256', bytes(accessToken)))
    const entrada = `${base64url(bytes(JSON.stringify(header)))}.${base64url(bytes(JSON.stringify(claims)))}`
    // ECDSA do WebCrypto já devolve IEEE P1363 (r‖s) — exatamente a assinatura ES256 do JWS, sem converter de DER.
    const assinatura = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, chave.par.privateKey, bytes(entrada))
    return { tipo: 'prova', prova: `${entrada}.${base64url(assinatura)}` }
  } catch (erro) {
    console.warn('Prova DPoP não montada.', erro instanceof Error || erro instanceof DOMException ? erro.name : 'erro desconhecido')
    return { tipo: 'falhou' }
  }
}

function jwkPublico(publica: CryptoKey): Promise<JwkPublico> {
  let jwk = jwkPorChave.get(publica)
  if (!jwk) {
    jwk = crypto.subtle.exportKey('jwk', publica).then(({ kty, crv, x, y }) => {
      if (!kty || !crv || !x || !y) throw new Error('jwk público incompleto')
      return { kty, crv, x, y }
    })
    jwkPorChave.set(publica, jwk)
  }
  return jwk
}

/**
 * `htu` da prova: a URL que o axios chama de fato, `origin + pathname` (sem query nem fragment, faixa 33 §3).
 * `combineURLs` como o axios (`lib/helpers/combineURLs.js`: tira a barra duplicada); url absoluta vence a base;
 * `new URL` codifica acento como o navegador e normaliza host e porta default. Base que não dá URL http(s)
 * absoluta (env ausente) → `null`: quem chama segue sem prova.
 */
export function htuDe(base: string | undefined, caminho: string | undefined): string | null {
  const alvo = caminho ?? ''
  const completa = ehAbsoluta(alvo) || !base ? alvo : juntar(base, alvo)
  try {
    const url = new URL(completa)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    return url.origin + url.pathname
  } catch {
    return null
  }
}

function ehAbsoluta(url: string): boolean {
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)
}

function juntar(base: string, relativa: string): string {
  if (!relativa) return base
  return `${base.replace(/\/+$/, '')}/${relativa.replace(/^\/+/, '')}`
}

function bytes(texto: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(texto)
}

function base64url(dados: ArrayBuffer | Uint8Array): string {
  const vetor = dados instanceof Uint8Array ? dados : new Uint8Array(dados)
  let binario = ''
  for (const byte of vetor) binario += String.fromCharCode(byte)
  return btoa(binario).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Só teste. */
export function __resetNonce(): void {
  nonce = null
  try {
    localStorage.removeItem(CHAVE_DO_STORAGE)
  } catch {
    // teste com storage bloqueado de propósito
  }
}

/** Só teste: o que um recarregamento de página faz com o módulo. */
export function __esquecerNonceDaMemoria(): void {
  nonce = null
}
